import { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useDotLegalSnackbar, useStateSessionStorage } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useDotLegalMutation, useDotLegalPostGetQueryArray, useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { useDelayedValue } from "@common/hooks/useDebounce.hooks";
import { post, put } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { FilterOperatorUi, ListFilter, ListFilterOperator, SortDirection } from "@common/api/filterTypes";
import { FilterOption, FilterOptionType, RiskFieldGrouping } from "@common/components/filterPopover/FilterPopover.types";
import { GetBooleanData, GetDateData, GetListData, GetNumberData } from "@common/components/filterPopover/subMenues/SubMenuDataHelper";
import { DateFieldValue, getDateRangeFromValue } from "@common/components/filterPopover/subMenues/DateFieldSubMenu";
import { hasDateRange } from "@common/components/filterBar/filterOperatorMatching";
import { TableFields } from "@common/components/tableDisplayFields/TableFields.types";
import { usePageView } from "@common/pageView/PageView.hooks";
import { FilterPage, SessionStorageSavedView } from "@common/pageView/PageView.types";
import { useSavedViewQuery } from "@common/components/savedViews/SavedViewsMenu.hooks";
import { SavedView } from "@common/components/savedViews/SavedViewsMenu.types";
import { RiskScenariosViewModel } from "../../riskSettings/riskScenarios/RiskScenarios.types";
import { RiskAssessmentOverviewResult, RiskScenarioItem } from "../riskAssessmentsOverview/RiskAssessmentsOverview.types";
import { getRiskAssessmentSubjectUrl, toRiskAssessmentSubject } from "../riskAssessmentsOverview/RiskAssessmentsOverview.utils";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery";
import { useSelectableControlsForRisk } from "../riskControls/RiskControls.hooks";
import { useCustomListExport } from "../../customLists/customListView/useCustomListExport";
import { useUrlProvider } from "../../useUrlProvider";
import { getRiskLevelIndicatorChipStyle } from "../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";
import { RiskExposureMode } from "../riskExposure/RiskExposure.types";
import { isTerminalMitigationStatus, mitigationStatusTranslationKey, validStatusTargets } from "../riskMitigationStatus";
import {
    allMitigationStatuses,
    allRiskMitigationColumns,
    backendColumnToRisk,
    defaultRiskMitigationFilter,
    mitigationColumnToBackend,
    RiskMitigationColumn,
    RiskMitigationExportHeader,
    RiskMitigationFilterRequest,
    RiskMitigationItem,
    RiskMitigationPlanStatus,
    RiskMitigationSavedFilter,
    RiskMitigationSearchResult,
} from "./RiskMitigationOverview.types";
import { shownTreatmentStatuses } from "./RiskMitigationOverview.utils";
import {
    controlStatusFilterOptions,
    frameworkControlFilterOptions,
    fromBoolFilter,
    organisationTypeFilterId,
    riskLevelFilterOptions,
    subjectTypeFilterOptions,
    toBoolFilter,
    toSubjectTypeFilter,
    toSubjectTypeRequest,
    yesNoFilterOptions,
} from "../riskFilterUtils";
import { EntityType } from "@common/Common.types";
import { boolParam, useSeedFilterFromUrl } from "@common/hooks/useSeedFilterFromUrl";

const defaultPageSize = 50;
const searchUrl = "/risk-areas/mitigation/search";
const promoteUrl = "/risk-areas/mitigation/promote";
const entitySearchUrl = "/risk-areas/operational/entities";

const statusFilterId = "statuses";
const responsibleFilterId = "responsibleIds";
const typeFilterId = "types";
const entityFilterId = "risk-entity-ids";
const scenarioFilterId = "scenarioIds";
const riskLevelFilterId = "riskLevels";
const expectedRiskLevelFilterId = "expectedRiskLevels";
const createdDateFilterId = "createdDate";
const hasOverdueDeadlineFilterId = "hasOverdueDeadline";
const hasLinkedControlsFilterId = "hasLinkedControls";
const frameworkControlFilterId = "frameworkControlIds";
const controlStatusFilterId = "controlStatuses";
const estimatedCostFilterId = "estimatedCost";
const estimatedLossFilterId = "estimatedLoss";
const exposureAfterMitigationFilterId = "exposureAfterMitigation";
const showOnlyMineFilterId = "show-only-mine";

const scenariosUrl = "/risk-scenarios";

function startOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function endOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
}

// Snaps the persisted date-range filter to whole-day boundaries so the request value is stable across renders
// within a day (a sub-day-varying range would change the React Query key every render); the end-of-day "to"
// also makes the chosen date inclusive. Mirrors the history overview's date handling.
function dateRangeToRequest(value: DateFieldValue | null): { createdFrom?: string; createdTo?: string } {
    if (!value) return {};

    const range = getDateRangeFromValue(value);
    if (!range) return {};

    return {
        createdFrom: range.from ? startOfDay(range.from).toISOString() : undefined,
        createdTo: range.to ? endOfDay(range.to).toISOString() : undefined,
    };
}

function toListFilter<T>(values: Array<T>, operator: FilterOperatorUi | undefined): ListFilter<T> | undefined {
    if (values.length === 0) return undefined;
    return { value: values, filterOperator: operator === FilterOperatorUi.Negative ? ListFilterOperator.NotIn : ListFilterOperator.In };
}

function extractIds(filter: ListFilter<unknown> | undefined): Array<string> {
    return (filter?.value ?? []).map((value) => String(value));
}

function extractOperator(filter: ListFilter<unknown> | undefined): FilterOperatorUi {
    return filter?.filterOperator === ListFilterOperator.NotIn ? FilterOperatorUi.Negative : FilterOperatorUi.Positive;
}

function isMitigationColumn(column: string): column is RiskMitigationColumn {
    return allRiskMitigationColumns.includes(column as RiskMitigationColumn);
}

// The saved filter's paging/sorting keys are always present, so they say nothing about whether the user narrowed
// the list; every other key does.
const nonNarrowingFilterKeys: Array<string> = ["pageNumber", "sortByColumn", "sortDirection"];

function isFilterValueSet(value: unknown): boolean {
    if (value === undefined || value === null || value === "") return false;
    if (typeof value === "boolean") return value;
    if (Array.isArray(value)) return value.length > 0;
    // Range filters (date, number interval) and list filters are objects — set when any of their parts is.
    if (typeof value === "object") return Object.values(value).some(isFilterValueSet);
    return true;
}

// Derived from the filter object rather than a hand-written per-field list: a newly added filter then counts
// automatically instead of silently making a zero-result page look like an area with no plans at all.
function hasNarrowingFilter(filter: RiskMitigationSavedFilter): boolean {
    return Object.entries(filter).some(([key, value]) => !nonNarrowingFilterKeys.includes(key) && isFilterValueSet(value));
}

// Treatment actions in the status menu, not plan statuses: accepting or avoiding the risk supersedes
// (cancels) the open plan via the treatment endpoint rather than moving the plan through its lifecycle.
const acceptRiskActionId = "accept-risk";
const avoidRiskActionId = "avoid-risk";
// Cancelling abandons the open plan (kept in history) and returns the risk to untreated via the
// treatment endpoint — no replacement treatment is chosen.
const cancelPlanActionId = "cancel-plan";
// Promote lives in the status menu (between the statuses and Cancel) rather than an overflow menu; it opens the
// same confirm dialog. Only a completed plan can actually promote — it stays visible-but-disabled otherwise.
const promoteActionId = "promote";

// The status menu groups mirror the picker design: the proposal-stage decisions first
// (propose / approve / defer), then the post-approval execution states. Switching away from
// Mitigate (accept / avoid) is no longer a plan status — it lives on the assessment treatment dialog.
const statusMenuGroups: Array<{ headerKey: string; statuses: Array<RiskMitigationPlanStatus> }> = [
    {
        headerKey: "riskMitigationStatusGroupDecision",
        statuses: [RiskMitigationPlanStatus.Proposed, RiskMitigationPlanStatus.Approved, RiskMitigationPlanStatus.Deferred],
    },
    {
        headerKey: "riskMitigationStatusGroupApproved",
        statuses: [RiskMitigationPlanStatus.Planned, RiskMitigationPlanStatus.InProgress, RiskMitigationPlanStatus.Completed],
    },
];

export interface MitigationStatusMenuItem {
    id: string;
    name: string;
    disabled: boolean;
    disabledHoverText?: string;
    isCurrent: boolean;
}

export interface MitigationStatusMenuGroup {
    // A null header renders the group's items as a plain list with no section heading.
    header: string | null;
    items: Array<MitigationStatusMenuItem>;
}

const sameStatusSet = (a: Array<string>, b: Array<string>) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

interface ChangeStatusVariables {
    riskAssessmentId: string;
    status: RiskMitigationPlanStatus;
    responsibleId?: string | null;
    deadline?: string | null;
    trackAsTask?: boolean;
}

interface PlanDialogState {
    riskAssessmentId: string;
    responsibleId: string | null;
    deadline: string | null;
    // A board task is related to the assessed entity, so an organisation-level plan has nothing to relate one to
    // and the backend refuses the opt-in. Hide the choice rather than offer one that cannot be honoured.
    canTrackAsTask: boolean;
}

interface AcceptDialogState {
    riskAssessmentId: string;
    planDescription: string;
}

interface PromoteResult {
    promoted: number;
    skipped: number;
}

export function useRiskMitigationOverview(riskAreaId: string | undefined) {
    const { translateString, translateFrameworkControlStatusEnum } = useTranslation();
    const snackbar = useDotLegalSnackbar();
    const queryClient = useDotLegalQueryClient();
    const { exportRiskMitigationInBackground } = useCustomListExport();
    const { getRiskAssessmentsOverviewUrl } = useUrlProvider();

    // The entity name links to that entity's risk-assessments page, same target as the operational overview.
    // Only an entity has a page of its own to link to; an organisation-level plan shows no link.
    const getItemUrl = (item: RiskMitigationItem) =>
        riskAreaId && item.entityType !== null && item.entityId !== null
            ? getRiskAssessmentsOverviewUrl(riskAreaId, item.entityType, item.entityId)
            : undefined;

    const searchStorageKey = `risk-mitigation-overview-search:${riskAreaId ?? ""}`;
    const [searchText, setSearchText] = useStateSessionStorage<string>(searchStorageKey, "");
    // Debounce the value that feeds the request key so a server-side search fires once the user pauses,
    // not on every keystroke. The searchbar still shows `searchText` immediately.
    const debouncedSearchText = useDelayedValue(searchText, 300);

    const savedViewQuery = useSavedViewQuery(FilterPage.RiskMitigationOverview, riskAreaId);
    const {
        currentViewId,
        getFilterData,
        getDisplayData,
        setFilterData,
        setDisplayData,
        clearFilterData,
        hasFilterChanged,
        hasDisplayChanged,
        isSavedView,
        savedView,
        setView,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = usePageView<RiskMitigationSavedFilter, Array<RiskMitigationColumn>>(FilterPage.RiskMitigationOverview, savedViewQuery.data, riskAreaId);

    const filterData = getFilterData<RiskMitigationSavedFilter>() ?? defaultRiskMitigationFilter;
    // The backend system-default view supplies the default columns once saved views resolve; until then the
    // table is in its loading state, so an empty fallback never renders.
    const visibleColumns = getDisplayData<Array<RiskMitigationColumn>>() ?? [];

    // Arriving from "Go to mitigation" carries statuses/scenarioIds/entityIds in the URL; the governance overdue
    // tile carries overdue=true and its gap figure withoutControls=true.
    useSeedFilterFromUrl(!savedViewQuery.isLoading, ["statuses", "scenarioIds", "entityIds", "withoutControls", "overdue"], (params) => {
        const statusesParam = params.get("statuses");
        const scenarioIdsParam = params.get("scenarioIds");
        const entityIdsParam = params.get("entityIds");
        const withoutControlsParam = params.get("withoutControls");
        const overdueParam = boolParam(params, "overdue");

        const seeded: RiskMitigationSavedFilter = { ...defaultRiskMitigationFilter };
        if (statusesParam)
            seeded.statuses = {
                value: statusesParam.split(",").map((x) => Number(x) as RiskMitigationPlanStatus),
                filterOperator: ListFilterOperator.In,
            };
        if (scenarioIdsParam) seeded.scenarioIds = { value: scenarioIdsParam.split(","), filterOperator: ListFilterOperator.In };
        if (entityIdsParam) seeded.entityIds = { value: entityIdsParam.split(","), filterOperator: ListFilterOperator.In };
        // The governance tile counts shown-status plans without linked controls, so the status filter comes along
        // to hide superseded history.
        if (withoutControlsParam) {
            seeded.hasLinkedControls = withoutControlsParam !== "true";
            seeded.statuses = { value: shownTreatmentStatuses, filterOperator: ListFilterOperator.In };
        }
        // No status filter alongside this one: the overdue filter is already restricted to the open statuses, so
        // seeding statuses too would only risk the two disagreeing.
        if (overdueParam !== undefined) seeded.hasOverdueDeadline = overdueParam;
        setFilterData(seeded);
    });

    const [planItem, setPlanItem] = useState<RiskMitigationItem | null>(null);
    const [planDialog, setPlanDialog] = useState<PlanDialogState | null>(null);
    const [acceptDialog, setAcceptDialog] = useState<AcceptDialogState | null>(null);
    const [avoidDialog, setAvoidDialog] = useState<{ riskAssessmentId: string } | null>(null);
    const [cancelDialog, setCancelDialog] = useState<{ riskAssessmentId: string } | null>(null);
    const [completeDialog, setCompleteDialog] = useState<{ riskAssessmentId: string; frameworkControlIds: Array<string> } | null>(null);
    const [promoteDialog, setPromoteDialog] = useState<{ items: Array<RiskMitigationItem> } | null>(null);

    const responsibleQuery = useResponsibleQuery();
    const scenarioQuery = useDotLegalQuery<RiskScenariosViewModel>(scenariosUrl);
    const controlsQuery = useSelectableControlsForRisk();

    const request: RiskMitigationFilterRequest = {
        riskAreaId: riskAreaId ?? "",
        showOnlyMine: filterData.showOnlyMine,
        statuses: filterData.statuses,
        responsibleIds: filterData.responsibleIds,
        ...toSubjectTypeRequest(filterData.types, filterData.includeOrganisation),
        entityIds: filterData.entityIds,
        scenarioIds: filterData.scenarioIds,
        riskLevels: filterData.riskLevels,
        expectedRiskLevels: filterData.expectedRiskLevels,
        ...dateRangeToRequest(filterData.createdDateRange ?? null),
        estimatedCostFrom: filterData.estimatedCostRange?.from,
        estimatedCostTo: filterData.estimatedCostRange?.to,
        estimatedLossFrom: filterData.estimatedLossRange?.from,
        estimatedLossTo: filterData.estimatedLossRange?.to,
        exposureAfterMitigationFrom: filterData.exposureAfterMitigationRange?.from,
        exposureAfterMitigationTo: filterData.exposureAfterMitigationRange?.to,
        hasOverdueDeadline: filterData.hasOverdueDeadline,
        hasLinkedControls: filterData.hasLinkedControls,
        frameworkControlIds: filterData.frameworkControlIds,
        controlStatuses: filterData.controlStatuses,
        searchText: debouncedSearchText.trim() || undefined,
        pageNumber: filterData.pageNumber,
        pageSize: defaultPageSize,
        sortByColumn: filterData.sortByColumn,
        sortDirection: filterData.sortDirection,
    };

    // keepPreviousData holds the prior page/KPIs on-screen while a filter or search change refetches, so the
    // summary cards don't flicker to 0 between fetches (they show the current numbers until new data lands).
    // isPlaceholderData is true only while a *key change* (search/filter) is fetching — not on a same-key
    // refetch after a mutation — so the table shows loading on search but never flickers on a treatment action.
    const { data, isLoading, isPlaceholderData } = useDotLegalPostGetQueryArray<RiskMitigationSearchResult>([searchUrl, request], searchUrl, request, {
        enabled: !savedViewQuery.isLoading,
        placeholderData: keepPreviousData,
    });

    // The mitigation plan dialog is opened from a row click: fetch the entity's scenarios (the same endpoint the
    // assessments overview uses) and pick the one this plan belongs to, so the dialog gets full scenario context.
    const detailUrl = planItem ? getRiskAssessmentSubjectUrl(riskAreaId ?? "", toRiskAssessmentSubject(planItem.entityType, planItem.entityId)) : "";
    const detailQuery = useDotLegalQuery<RiskAssessmentOverviewResult>(detailUrl, { enabled: !!planItem });
    const selectedScenario: RiskScenarioItem | null =
        planItem && detailQuery.data ? (detailQuery.data.scenarios.find((scenario) => scenario.id === planItem.riskAssessmentId) ?? null) : null;

    const changeStatusMutation = useDotLegalMutation<unknown, ChangeStatusVariables>(({ riskAssessmentId, ...body }) =>
        put(`/risk-assessments/${riskAssessmentId}/mitigation-plan/status`, body)
    );

    const acceptRiskMutation = useDotLegalMutation<unknown, { riskAssessmentId: string; reason: string; reviewBy: string | null }>(
        ({ riskAssessmentId, ...body }) => post(`/risk-assessments/${riskAssessmentId}/treatment/accept`, body)
    );

    const avoidRiskMutation = useDotLegalMutation<unknown, { riskAssessmentId: string; reason: string }>(({ riskAssessmentId, ...body }) =>
        post(`/risk-assessments/${riskAssessmentId}/treatment/avoid`, body)
    );

    const cancelPlanMutation = useDotLegalMutation<unknown, { riskAssessmentId: string }>(({ riskAssessmentId }) =>
        post(`/risk-assessments/${riskAssessmentId}/treatment/cancel`, {})
    );

    const promoteMutation = useDotLegalMutation<Result<PromoteResult>, { riskAssessmentIds: Array<string> }>((body) => post(promoteUrl, body));

    const closeDialogs = () => {
        setPlanDialog(null);
        setAcceptDialog(null);
        setAvoidDialog(null);
        setCancelDialog(null);
        setCompleteDialog(null);
        setPlanItem(null);
    };

    const runStatusChange = (variables: ChangeStatusVariables) => {
        changeStatusMutation.mutate(variables, {
            onSuccess: () => {
                snackbar.show(translateString("riskMitigationStatusChanged"));
                queryClient.refetchQueries(searchUrl);
                closeDialogs();
            },
        });
    };

    const openMitigationPlan = (item: RiskMitigationItem) => setPlanItem(item);

    // The dialog shows its own save snackbar (treatmentSaved/treatmentReconfirmed) and calls this purely to
    // refresh the list and close, so it must not re-show a snackbar.
    const onMitigationPlanSaved = () => {
        queryClient.refetchQueries(searchUrl);
        setPlanItem(null);
    };

    const responsibleOptions = (responsibleQuery.userData ?? []).map((x) => ({ id: x.id, name: x.name }));

    // Only scenarios that apply to this area can produce plans here, so the filter list is scoped to them.
    const scenarioOptions = (scenarioQuery.data?.items ?? [])
        .filter((scenario) => scenario.appliesToAllRiskAreas || scenario.riskAreas.some((area) => area.id === riskAreaId))
        .map((scenario) => ({ id: scenario.id, name: scenario.name }));

    const statusOptions = allMitigationStatuses.map((status) => ({
        id: status.toString(),
        name: translateString(mitigationStatusTranslationKey[status]),
    }));

    // The area's risk-level labels, shared here by both the current-risk and after-mitigation filters.
    const riskLevelOptions = riskLevelFilterOptions(data?.riskLevels);

    const yesNoOptions = yesNoFilterOptions(translateString);
    const frameworkControlOptions = frameworkControlFilterOptions(controlsQuery.data);
    const controlStatusOptions = controlStatusFilterOptions(translateFrameworkControlStatusEnum);

    const exposureMode = data?.exposureMode ?? RiskExposureMode.None;
    const exposureEnabled = exposureMode !== RiskExposureMode.None;

    const filterOptions: Array<FilterOption> = [
        {
            id: showOnlyMineFilterId,
            label: translateString("show"),
            type: FilterOptionType.Boolean,
            value: filterData.showOnlyMine ?? false,
            fixedData: [
                { id: "0", name: translateString("all") },
                { id: "1", name: translateString("my") },
            ],
        },
        {
            id: statusFilterId,
            label: translateString("planStatus"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.statuses),
            fixedData: statusOptions,
            filterOperator: extractOperator(filterData.statuses),
        },
        {
            id: typeFilterId,
            label: translateString("type"),
            type: FilterOptionType.SelectField,
            value: [...extractIds(filterData.types), ...(filterData.includeOrganisation ? [organisationTypeFilterId] : [])],
            fixedData: subjectTypeFilterOptions(translateString),
            filterOperator: extractOperator(filterData.types),
        },
        {
            id: entityFilterId,
            label: translateString("entity"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.entityIds),
            searchUrl: entitySearchUrl,
            filterOperator: extractOperator(filterData.entityIds),
        },
        {
            id: scenarioFilterId,
            label: translateString("scenario"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.scenarioIds),
            fixedData: scenarioOptions,
            filterOperator: extractOperator(filterData.scenarioIds),
        },
        {
            id: responsibleFilterId,
            label: translateString("responsible"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.responsibleIds),
            fixedData: responsibleOptions,
            filterOperator: extractOperator(filterData.responsibleIds),
        },
        // The two risk chips the table renders: the assessment's current level and the plan's expected one, both
        // offered on the area's own labels like the operational overview's risk filter.
        {
            id: riskLevelFilterId,
            label: translateString("risk"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.riskLevels),
            fixedData: riskLevelOptions,
            filterOperator: extractOperator(filterData.riskLevels),
        },
        {
            id: expectedRiskLevelFilterId,
            label: translateString("afterMitigation"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.expectedRiskLevels),
            fixedData: riskLevelOptions,
            filterOperator: extractOperator(filterData.expectedRiskLevels),
        },
        {
            id: createdDateFilterId,
            label: translateString("created"),
            type: FilterOptionType.DateField,
            value: filterData.createdDateRange ?? "",
            hideFutureDates: true,
        },
        // The plan's own deadline, not a control signal: open and past due.
        {
            id: hasOverdueDeadlineFilterId,
            label: translateString("overdueDeadline"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filterData.hasOverdueDeadline),
            fixedData: yesNoOptions,
        },
        // The plan links no controls at all: the overdue tile's "plans without control coverage" landing set.
        {
            id: hasLinkedControlsFilterId,
            label: translateString("hasLinkedControls"),
            type: FilterOptionType.SelectField,
            value: fromBoolFilter(filterData.hasLinkedControls),
            fixedData: yesNoOptions,
        },
        // Plan-scoped control filters mirroring the operational overview's: per-control status and links to
        // specific controls, both over the plan's own scope.
        {
            id: frameworkControlFilterId,
            label: translateString("controls"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.frameworkControlIds),
            fixedData: frameworkControlOptions,
            filterOperator: extractOperator(filterData.frameworkControlIds),
        },
        {
            id: controlStatusFilterId,
            label: translateString("controlStatus"),
            type: FilterOptionType.SelectField,
            value: extractIds(filterData.controlStatuses),
            fixedData: controlStatusOptions,
            filterOperator: extractOperator(filterData.controlStatuses),
        },
        ...(exposureEnabled
            ? [
                  {
                      id: estimatedCostFilterId,
                      label: translateString("estimatedCost"),
                      type: FilterOptionType.Number,
                      value: filterData.estimatedCostRange ?? { from: undefined, to: undefined },
                  },
                  {
                      id: estimatedLossFilterId,
                      label: translateString("riskExposure"),
                      type: FilterOptionType.Number,
                      value: filterData.estimatedLossRange ?? { from: undefined, to: undefined },
                  },
                  {
                      id: exposureAfterMitigationFilterId,
                      label: translateString("exposureAfterMitigationLabel"),
                      type: FilterOptionType.Number,
                      value: filterData.exposureAfterMitigationRange ?? { from: undefined, to: undefined },
                  },
              ]
            : []),
    ];

    const onFilterChange = (updatedOptions: Array<FilterOption>) => {
        const next: RiskMitigationSavedFilter = { ...filterData, pageNumber: 1 };
        updatedOptions.forEach((option) => {
            if (option.id === showOnlyMineFilterId) {
                next.showOnlyMine = GetBooleanData(option.value) === "1";
                return;
            }
            if (option.id === createdDateFilterId) {
                const dateValue = GetDateData(option.value);
                const hasRange = hasDateRange(dateValue);
                // Remove the key when cleared (rather than storing null) so the shape matches a saved view's
                // filter data — otherwise a phantom createdDateRange key trips change detection on every close.
                if (hasRange) next.createdDateRange = dateValue;
                else delete next.createdDateRange;
                return;
            }
            if (option.type === FilterOptionType.Number) {
                const numberValue = GetNumberData(option.value);
                const range = numberValue.from !== undefined || numberValue.to !== undefined ? numberValue : undefined;
                switch (option.id) {
                    case estimatedCostFilterId:
                        if (range) next.estimatedCostRange = range;
                        else delete next.estimatedCostRange;
                        break;
                    case estimatedLossFilterId:
                        if (range) next.estimatedLossRange = range;
                        else delete next.estimatedLossRange;
                        break;
                    case exposureAfterMitigationFilterId:
                        if (range) next.exposureAfterMitigationRange = range;
                        else delete next.exposureAfterMitigationRange;
                        break;
                }
                return;
            }

            const values = GetListData(option.value);
            switch (option.id) {
                case statusFilterId:
                    next.statuses = toListFilter(
                        values.map((x) => Number(x) as RiskMitigationPlanStatus),
                        option.filterOperator
                    );
                    break;
                case responsibleFilterId:
                    next.responsibleIds = toListFilter(values, option.filterOperator);
                    break;
                case typeFilterId: {
                    // Not toListFilter: the organisation shares this selection, so the entity list can empty out
                    // while the filter still holds an operator that has to survive.
                    const subjectTypes = toSubjectTypeFilter(values, option.filterOperator);
                    next.types = subjectTypes.types;
                    next.includeOrganisation = subjectTypes.includeOrganisation;
                    break;
                }
                case entityFilterId:
                    next.entityIds = toListFilter(values, option.filterOperator);
                    break;
                case scenarioFilterId:
                    next.scenarioIds = toListFilter(values, option.filterOperator);
                    break;
                case riskLevelFilterId:
                    next.riskLevels = toListFilter(values.map(Number), option.filterOperator);
                    break;
                case expectedRiskLevelFilterId:
                    next.expectedRiskLevels = toListFilter(values.map(Number), option.filterOperator);
                    break;
                case hasOverdueDeadlineFilterId:
                    next.hasOverdueDeadline = toBoolFilter(values);
                    break;
                case hasLinkedControlsFilterId:
                    next.hasLinkedControls = toBoolFilter(values);
                    break;
                case frameworkControlFilterId:
                    next.frameworkControlIds = toListFilter(values, option.filterOperator);
                    break;
                case controlStatusFilterId:
                    next.controlStatuses = toListFilter(values.map(Number), option.filterOperator);
                    break;
            }
        });

        // Drop cleared (undefined) filters so the persisted shape matches a saved view's JSON round-tripped filter
        // data; otherwise change detection compares against phantom keys and the reset button never clears.
        const cleaned = next as unknown as Record<string, unknown>;
        Object.keys(cleaned).forEach((key) => {
            if (cleaned[key] === undefined) delete cleaned[key];
        });

        setFilterData(next);
    };

    // KPI cards act as single-select status shortcuts: clicking one sets the status filter to exactly that
    // card's statuses, clicking the active card again clears the status filter (the default shows all statuses).
    const isKpiActive = (statuses: Array<number>) => sameStatusSet(extractIds(filterData.statuses), statuses.map(String));

    const onKpiFilter = (statuses: Array<number>) => {
        const nextStatuses = isKpiActive(statuses)
            ? defaultRiskMitigationFilter.statuses
            : { value: statuses.map((x) => x as RiskMitigationPlanStatus), filterOperator: ListFilterOperator.In };
        setFilterData({ ...filterData, statuses: nextStatuses, pageNumber: 1 });
    };

    const onSearchTextChange = (value: string) => {
        setSearchText(value ?? "");
        setFilterData({ ...filterData, pageNumber: 1 });
    };

    const onSortChange = (column: RiskMitigationColumn, direction: SortDirection) => {
        setFilterData({ ...filterData, sortByColumn: mitigationColumnToBackend[column], sortDirection: direction, pageNumber: 1 });
    };

    const onPageChange = (page: number) => {
        setFilterData({ ...filterData, pageNumber: page });
    };

    const onColumnsSave = (columns: Array<string>) => {
        setDisplayData(columns.filter(isMitigationColumn));
    };

    const applyView = (viewId: string | undefined, filter: RiskMitigationSavedFilter, display: Array<RiskMitigationColumn>) => {
        const updatedView: SessionStorageSavedView = {
            page: FilterPage.RiskMitigationOverview,
            viewId,
            filterData: filter,
            displayData: display,
        };
        setView(updatedView);
    };

    const onViewSaved = (viewId: string) => {
        applyView(viewId, filterData, visibleColumns);
    };

    const onViewDeleted = (deletedViewId: string) => {
        if (deletedViewId !== currentViewId) return;

        const fallbackView = savedViewQuery.data?.find((x: SavedView) => x.isCustomerDefault || x.isSystemDefault);
        applyView(fallbackView?.id === deletedViewId ? undefined : fallbackView?.id, filterData, visibleColumns);
    };

    const onChangeView = (view: SavedView) => {
        if (view.id !== currentViewId) {
            applyView(view.id, view.filterData, view.displayData);
        }
    };

    const tableFields: Array<TableFields> = [
        { id: RiskMitigationColumn.Name, name: translateString("name"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.Action, name: translateString("riskMitigationColumnAction"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.Type, name: translateString("type"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.Entity, name: translateString("entity"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.Scenario, name: translateString("scenario"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.CurrentRisk, name: translateString("risk"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.ExpectedRisk, name: translateString("afterMitigation"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.Responsible, name: translateString("responsible"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.CreatedDate, name: translateString("created"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.Deadline, name: translateString("deadline"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.Status, name: translateString("planStatus"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.ControlCount, name: translateString("controlCount"), grouping: RiskFieldGrouping.Risk },
        { id: RiskMitigationColumn.ControlStatus, name: translateString("controlStatus"), grouping: RiskFieldGrouping.Risk },
        ...(exposureEnabled
            ? [
                  { id: RiskMitigationColumn.EstimatedCost, name: translateString("estimatedCost"), grouping: RiskFieldGrouping.Risk },
                  { id: RiskMitigationColumn.EstimatedLoss, name: translateString("riskExposure"), grouping: RiskFieldGrouping.Risk },
                  { id: RiskMitigationColumn.ExposureAfterMitigation, name: translateString("exposureAfterMitigationLabel"), grouping: RiskFieldGrouping.Risk },
              ]
            : []),
    ];

    // Render only persisted columns that are still selectable. tableFields omits the exposure columns when
    // exposure is not configured, so this drops them from the system-default view instead of showing a blank.
    // While the data (and thus the exposure mode) is still loading, tableFields hasn't yet gained the exposure
    // columns, so keep the selected columns as-is until we know the configuration to avoid transiently dropping
    // a selected exposure column from the header.
    const renderedColumns = isLoading ? visibleColumns : visibleColumns.filter((column) => tableFields.some((field) => field.id === column));

    const onDownloadExcelReportClick = async () => {
        const headerByColumn = new Map(tableFields.map((field) => [field.id, field.name]));
        const headers: Array<RiskMitigationExportHeader> = renderedColumns.map((column) => ({
            header: headerByColumn.get(column) ?? "",
            column,
        }));

        await exportRiskMitigationInBackground(savedView!.name, headers, request);
        snackbar.show(translateString("riskOperationalDataExportInProgress"), "success", true);
    };

    // The full lifecycle is shown, grouped to match the picker design; statuses that aren't a valid
    // single-step transition from the row's current status are disabled and carry a tooltip explaining why.
    const getStatusMenuGroups = (item: RiskMitigationItem): Array<MitigationStatusMenuGroup> => {
        const disabledHoverText = translateString("riskMitigationStatusOptionDisabled");

        const toItem = (status: RiskMitigationPlanStatus): MitigationStatusMenuItem => {
            const disabled = status !== item.status && !(validStatusTargets[item.status] ?? []).includes(status);

            return {
                id: status.toString(),
                name: translateString(mitigationStatusTranslationKey[status]),
                disabled,
                disabledHoverText: disabled ? disabledHoverText : undefined,
                isCurrent: status === item.status,
            };
        };

        // A completed plan is a dead-end except for promotion: its transitions, cancel, and treatment switches are
        // all shown-but-disabled so only Promote is actionable.
        const treatmentDisabled = item.status === RiskMitigationPlanStatus.Completed;

        return [
            // The status transitions render headerless as one flat list; the lifecycle order is still carried by
            // statusMenuGroups, the section headings were just dropped.
            ...statusMenuGroups.map((group) => ({
                header: null,
                items: group.statuses.map(toItem),
            })),
            // Promote sits between the statuses (after Completed) and Cancel. Hidden once already promoted;
            // otherwise visible-but-disabled until the plan is completed (only completed plans can promote).
            ...(item.status === RiskMitigationPlanStatus.Promoted
                ? []
                : [
                      {
                          header: null,
                          items: [
                              {
                                  id: promoteActionId,
                                  name: translateString("promote"),
                                  disabled: !canPromote(item),
                                  disabledHoverText: canPromote(item) ? undefined : translateString("riskMitigationPromoteDisabled"),
                                  isCurrent: false,
                              },
                          ],
                      },
                  ]),
            {
                // Cancel is the last status action — headerless and contiguous with the statuses, immediately
                // before the treatment section heading.
                header: null,
                items: [
                    {
                        id: cancelPlanActionId,
                        name: translateString("riskMitigationActionCancelPlan"),
                        // A completed plan is no longer open, so it cannot be cancelled — only open plans can.
                        disabled: isTerminalMitigationStatus(item.status),
                        disabledHoverText: isTerminalMitigationStatus(item.status) ? translateString("riskMitigationCancelPlanDisabled") : undefined,
                        isCurrent: false,
                    },
                ],
            },
            {
                header: translateString("riskMitigationStatusGroupTreatment"),
                items: [
                    {
                        id: acceptRiskActionId,
                        name: translateString("riskMitigationActionAcceptRisk"),
                        disabled: treatmentDisabled,
                        disabledHoverText: treatmentDisabled ? translateString("riskMitigationStatusOptionDisabled") : undefined,
                        isCurrent: false,
                    },
                    {
                        id: avoidRiskActionId,
                        name: translateString("riskTreatmentTypeAvoid"),
                        disabled: treatmentDisabled,
                        disabledHoverText: treatmentDisabled ? translateString("riskMitigationStatusOptionDisabled") : undefined,
                        isCurrent: false,
                    },
                ],
            },
        ];
    };

    const onStatusSelect = (item: RiskMitigationItem, selectedId: string) => {
        if (selectedId === promoteActionId) {
            openPromote([item]);
            return;
        }

        if (selectedId === acceptRiskActionId) {
            setAcceptDialog({ riskAssessmentId: item.riskAssessmentId, planDescription: item.description });
            return;
        }

        if (selectedId === avoidRiskActionId) {
            setAvoidDialog({ riskAssessmentId: item.riskAssessmentId });
            return;
        }

        if (selectedId === cancelPlanActionId) {
            setCancelDialog({ riskAssessmentId: item.riskAssessmentId });
            return;
        }

        const target = Number(selectedId) as RiskMitigationPlanStatus;
        if (target === item.status) return;

        if (target === RiskMitigationPlanStatus.Planned && item.status === RiskMitigationPlanStatus.Approved) {
            setPlanDialog({
                riskAssessmentId: item.riskAssessmentId,
                responsibleId: item.responsibleId,
                deadline: item.deadline,
                canTrackAsTask: item.entityId !== null,
            });
            return;
        }

        if (target === RiskMitigationPlanStatus.Completed) {
            openCompleteConfirmation(item);
            return;
        }

        runStatusChange({ riskAssessmentId: item.riskAssessmentId, status: target });
    };

    // Completing graduates the plan's controls onto the risk's standing coverage, so a plan with controls gets a
    // confirmation disclosing that side effect (with each control's status chip). A plan without controls
    // completes directly — today's one-click behaviour, since there is nothing to disclose.
    const openCompleteConfirmation = (item: RiskMitigationItem) => {
        if (item.linkedControlIds.length === 0) {
            runStatusChange({ riskAssessmentId: item.riskAssessmentId, status: RiskMitigationPlanStatus.Completed });
            return;
        }

        setCompleteDialog({ riskAssessmentId: item.riskAssessmentId, frameworkControlIds: item.linkedControlIds });
    };

    const submitCompletePlan = () => {
        if (!completeDialog) return;
        runStatusChange({ riskAssessmentId: completeDialog.riskAssessmentId, status: RiskMitigationPlanStatus.Completed });
    };

    const submitPlan = (responsibleId: string, deadline: Date, trackAsTask: boolean) => {
        if (!planDialog) return;
        runStatusChange({
            riskAssessmentId: planDialog.riskAssessmentId,
            status: RiskMitigationPlanStatus.Planned,
            responsibleId,
            deadline: deadline.toISOString(),
            trackAsTask,
        });
    };

    const submitAcceptRisk = (reason: string, reviewBy: Date | null) => {
        if (!acceptDialog) return;
        acceptRiskMutation.mutate(
            { riskAssessmentId: acceptDialog.riskAssessmentId, reason, reviewBy: reviewBy ? reviewBy.toISOString() : null },
            {
                onSuccess: () => {
                    snackbar.show(translateString("riskMitigationStatusChanged"));
                    queryClient.refetchQueries(searchUrl);
                    closeDialogs();
                },
            }
        );
    };

    const submitAvoidRisk = (reason: string) => {
        if (!avoidDialog) return;
        avoidRiskMutation.mutate(
            { riskAssessmentId: avoidDialog.riskAssessmentId, reason },
            {
                onSuccess: () => {
                    snackbar.show(translateString("riskMitigationStatusChanged"));
                    queryClient.refetchQueries(searchUrl);
                    closeDialogs();
                },
            }
        );
    };

    const submitCancelPlan = () => {
        if (!cancelDialog) return;
        cancelPlanMutation.mutate(
            { riskAssessmentId: cancelDialog.riskAssessmentId },
            {
                onSuccess: () => {
                    snackbar.show(translateString("riskMitigationPlanCancelled"));
                    queryClient.refetchQueries(searchUrl);
                    closeDialogs();
                },
            }
        );
    };

    // Only a completed plan can be promoted; the backend re-validates and skips anything else, but the UI gates
    // both the per-row action and bulk selection on this so the user can't pick what won't promote.
    const canPromote = (item: RiskMitigationItem) => item.status === RiskMitigationPlanStatus.Completed;

    const openPromote = (items: Array<RiskMitigationItem>) => setPromoteDialog({ items });
    const closePromote = () => setPromoteDialog(null);

    // Selecting non-completed plans is blocked in the UI, so a skip only happens when a plan's state changed
    // between selection and submit (a concurrency race). When that happens, warn with the reason and how many,
    // and let the list refresh below surface the corrected state.
    const submitPromote = (riskAssessmentIds: Array<string>, onPromoted?: () => void) => {
        promoteMutation.mutate(
            { riskAssessmentIds },
            {
                onSuccess: (result) => {
                    const { promoted, skipped } = result.value();
                    if (skipped > 0)
                        snackbar.show(
                            `${translateString("riskMitigationPromoted", { count: promoted })} · ${translateString("riskMitigationPromoteSkipped", { count: skipped })}`,
                            "warning"
                        );
                    else snackbar.show(translateString("riskMitigationPromoted", { count: promoted }));
                    queryClient.refetchQueries(searchUrl);
                    closePromote();
                    onPromoted?.();
                },
            }
        );
    };

    const getRiskChipStyle = (level: number) => getRiskLevelIndicatorChipStyle(level, data?.riskLevelIndicators ?? []);

    // Distinguishes a genuinely empty area (no plans at all → show the empty state) from a filter/search that
    // simply matched nothing (→ keep the table with its "no matches" message). The default filter carries no
    // status/responsible/etc. selection, so any set filter or a non-empty search means the user narrowed the list.
    const hasActiveFilters = hasNarrowingFilter(filterData) || debouncedSearchText.trim().length > 0;

    // Mirrors the assessments overview's hasScenarios: the table owns the loading state, so keep showing it while
    // loading or whenever the area has plans; only a genuinely empty area falls through to the empty state. A
    // filter/search that matched nothing keeps the table (with its "no matches" message) rather than the empty state.
    const hasPlans = isLoading || savedViewQuery.isLoading || (data?.totalCount ?? 0) > 0 || hasActiveFilters;

    const kpis = data?.kpis;

    return {
        pinnedFilterIds,
        setPinnedFilterIds,
        result: data,
        kpis,
        isLoading: isLoading || savedViewQuery.isLoading,
        isPlaceholderData,
        hasPlans,
        pageNumber: filterData.pageNumber,
        pageSize: defaultPageSize,
        sortByColumn: backendColumnToRisk[filterData.sortByColumn] ?? RiskMitigationColumn.CreatedDate,
        sortDirection: filterData.sortDirection,
        onSortChange,
        onPageChange,
        filterOptions,
        onFilterChange,
        clearFilterData,
        isKpiActive,
        onKpiFilter,
        searchText,
        onSearchTextChange,
        visibleColumns,
        renderedColumns,
        tableFields,
        hasDisplayChanged,
        hasFilterChanged,
        isSavedView,
        savedView,
        currentViewId,
        filterData,
        onColumnsSave,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        onDownloadExcelReportClick,
        getRiskChipStyle,
        getItemUrl,
        getStatusMenuGroups,
        onStatusSelect,
        isChangingStatus: changeStatusMutation.isLoading || acceptRiskMutation.isLoading || avoidRiskMutation.isLoading || cancelPlanMutation.isLoading,
        openMitigationPlan,
        selectedScenario,
        selectedPlanId: planItem?.planId,
        onMitigationPlanSaved,
        planDialog,
        acceptDialog,
        avoidDialog,
        cancelDialog,
        completeDialog,
        closeDialogs,
        submitPlan,
        submitAcceptRisk,
        submitAvoidRisk,
        submitCancelPlan,
        submitCompletePlan,
        canPromote,
        openPromote,
        closePromote,
        submitPromote,
        promoteDialog,
        isPromoting: promoteMutation.isLoading,
        responsibleOptions,
        isLoadingResponsibles: responsibleQuery.isLoading,
    };
}

export type UseRiskMitigationOverview = ReturnType<typeof useRiskMitigationOverview>;
export type { RiskMitigationItem };
